import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import type { BookingReservationDetail, ClientOption } from '@/types';

import {
  BookingDialogBody,
  BookingDialogFooter,
  ClientAutoComplete,
} from '@/components';
import { Button } from '@/components/ui/button';

import { bookingCoverageCandidateQueries } from '@/queries';

import { useAttachBookingReservationMutation } from '@/mutations';

import { notifyBookingSuccess, notifyMutationError } from '@/helpers';

import { CoverageChoice, NO_COVERAGE } from '../ReservationDetailDialog';

type EventAttendanceFormProps = {
  reservation: BookingReservationDetail;
  reservationsUrl: string;
  clientsSearchUrl: string;
  clientsUrl: string;
  newClientsUrl: string;
  onClose: () => void;
  onSuccess: () => void;
};

export const EventAttendanceForm = (props: EventAttendanceFormProps) => {
  const {
    reservation,
    reservationsUrl,
    clientsSearchUrl,
    clientsUrl,
    newClientsUrl,
    onClose,
    onSuccess,
  } = props;
  const { t } = useTranslation();

  const [pickedClient, setPickedClient] = useState<ClientOption | null>(
    reservation.client,
  );
  const [coverage, setCoverage] = useState<string>(NO_COVERAGE);

  const isPrepaid = reservation.coverage !== null;
  const isUnnamed = reservation.client === null;
  const clientId = pickedClient?.id ?? null;

  const onClientChange = useCallback((client: ClientOption | null) => {
    setPickedClient(client);
    setCoverage(NO_COVERAGE);
  }, []);

  const { data: candidates, isLoading } = useQuery({
    ...bookingCoverageCandidateQueries.list({
      url: reservationsUrl,
      reservationId: reservation.id,
      clientId,
    }),
    enabled: !isPrepaid && clientId !== null,
    refetchOnWindowFocus: true,
  });

  const serviceCandidates = useMemo(
    () => ({
      client_cards: [],
      client_services: (candidates?.client_services ?? []).filter(
        (service) => service.error !== 'coverage_already_held',
      ),
    }),
    [candidates?.client_services],
  );

  const hasChoice = !isPrepaid && serviceCandidates.client_services.length > 0;

  const clientCardUrl = reservation.client?.is_new
    ? `${newClientsUrl}/${reservation.client.id}/edit`
    : `${clientsUrl}/${clientId}?tab=general`;

  const { mutate: attach, isPending } = useAttachBookingReservationMutation();

  const coverageData = useMemo(() => {
    const [kind, rawId] = coverage.split(':');

    return kind === 'service' ? { client_service_id: Number(rawId) } : {};
  }, [coverage]);

  const onConfirm = useCallback(() => {
    attach(
      {
        url: `${reservationsUrl}/${reservation.id}`,
        data: { client_id: clientId, use_now: true, ...coverageData },
      },
      {
        onSuccess: (response) => {
          notifyBookingSuccess(response);
          onSuccess();
          onClose();
        },
        onError: notifyMutationError,
      },
    );
  }, [
    attach,
    clientId,
    coverageData,
    onClose,
    onSuccess,
    reservation.id,
    reservationsUrl,
  ]);

  return (
    <>
      <BookingDialogBody>
        <p className="text-sm font-medium">{t('Confirm attendance')}</p>

        {isUnnamed && (
          <ClientAutoComplete
            fetchUrl={clientsSearchUrl}
            queryKey={['event_attendance_client']}
            label={t('Who attended')}
            value={pickedClient}
            onChange={onClientChange}
          />
        )}

        <div className="rounded-md border p-3 text-sm">
          {isPrepaid && (
            <>
              <p className="font-medium">{t('Paid online')}</p>
              <p className="text-muted-foreground">
                {t('The purchased service is recognised on confirmation', {
                  service: reservation.service.name,
                })}
              </p>
            </>
          )}

          {!isPrepaid && (
            <>
              <p className="font-medium">{t('Place hold')}</p>
              <p className="text-muted-foreground">
                {t(
                  'Nothing is charged — money for an event is a separate sale',
                )}
              </p>

              {clientId !== null && !hasChoice && (
                <Button
                  variant="outline"
                  className="mt-2"
                  asChild
                >
                  <a
                    href={clientCardUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('Sell on the client card')}
                  </a>
                </Button>
              )}
            </>
          )}
        </div>

        {hasChoice && (
          <CoverageChoice
            candidates={serviceCandidates}
            isLoading={isLoading}
            value={coverage}
            onChange={setCoverage}
          />
        )}
      </BookingDialogBody>

      <BookingDialogFooter
        submitLabel={t('Confirm attendance')}
        isPending={isPending}
        onClose={onClose}
        onSubmit={onConfirm}
      />
    </>
  );
};
